import React, { useCallback, useEffect } from "react";
import icons from "../../../../../assets/images/icons";
import { useEscapeClose } from "../../../../../common/hooks/useEscapeClose";
import { downloadChatFile } from "../../../apis/chatApi";
import { pickChatImageDownloadFileName } from "../../../utils/chatDownloadFileName";
import styles from "./ChatImagePreviewModal.module.scss";

/**
 * 채팅 이미지 풀스크린 미리보기 (대화 상세 .root 내부 absolute)
 * @param {{ urls: string[], index: number, names?: (string|null)[] } | null} preview
 */
function ChatImagePreviewModal({ preview, onClose, onStep }) {
  const step = useCallback(
    (delta) => {
      onStep?.(delta);
    },
    [onStep]
  );

  useEscapeClose(onClose, Boolean(preview));

  useEffect(() => {
    if (!preview) return;
    const onKeyDown = (e) => {
      if (preview.urls.length < 2) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        step(-1);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        step(1);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [preview, step]);

  if (!preview) return null;

  const { urls, index, names, downloadUrls } = preview;

  return (
    <div
      className={styles.imageModalOverlay}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="이미지 상세보기"
      data-node-id="40004313:33192"
    >
      <div className={styles.imageModal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.imageModalNav} data-node-id="40004313:33290">
          <div className={styles.imageModalHeader} data-node-id="40004313:33298">
            <h2 className={styles.imageModalTitle} data-node-id="40004313:33306">
              {urls.length > 1 ? `${index + 1} / ${urls.length}` : "이미지 상세보기"}
            </h2>
            <button
              type="button"
              className={styles.imageModalClose}
              onClick={onClose}
              aria-label="닫기"
              data-node-id="40004313:33309"
            >
              {icons.messager?.close && (
                <img src={icons.messager.close} alt="" width={24} height={24} />
              )}
            </button>
          </div>
          <div className={styles.imageModalHeaderDivider} />
        </div>
        <div
          className={styles.imageModalContent}
          data-node-id="40004313:33314"
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          {urls.length > 1 && (
            <button
              type="button"
              className={`${styles.imageModalArrow} ${styles.imageModalArrowPrev}`}
              onClick={(e) => {
                e.stopPropagation();
                step(-1);
              }}
              disabled={index <= 0}
              aria-label="이전 이미지"
            >
              {icons.messager?.chevronLeft && (
                <img
                  src={icons.messager.chevronLeft}
                  alt=""
                  className={styles.imageModalArrowIcon}
                />
              )}
            </button>
          )}
          <img
            key={urls[index]}
            src={urls[index]}
            alt=""
            className={styles.imageModalImg}
            data-node-id="40004313:33315"
            onClick={(e) => {
              const img = e.currentTarget;
              const {
                naturalWidth: nw,
                naturalHeight: nh,
                clientWidth: cw,
                clientHeight: ch,
              } = img;
              if (!nw || !nh) return;
              const scale = Math.min(cw / nw, ch / nh);
              const vw = nw * scale;
              const vh = nh * scale;
              const offsetX = (cw - vw) / 2;
              const offsetY = (ch - vh) / 2;
              const { offsetX: x, offsetY: y } = e.nativeEvent;
              const outside = x < offsetX || x > offsetX + vw || y < offsetY || y > offsetY + vh;
              if (outside) onClose();
            }}
          />
          {urls.length > 1 && (
            <button
              type="button"
              className={`${styles.imageModalArrow} ${styles.imageModalArrowNext}`}
              onClick={(e) => {
                e.stopPropagation();
                step(1);
              }}
              disabled={index >= urls.length - 1}
              aria-label="다음 이미지"
            >
              {icons.messager?.chevronRight && (
                <img
                  src={icons.messager.chevronRight}
                  alt=""
                  className={styles.imageModalArrowIcon}
                />
              )}
            </button>
          )}
        </div>
        <button
          type="button"
          className={styles.imageDownloadBtn}
          onClick={() => {
            const dlUrl = Array.isArray(downloadUrls) && downloadUrls[index] ? downloadUrls[index] : urls[index];
            const suggested = pickChatImageDownloadFileName(names?.[index], dlUrl, index);
            downloadChatFile(dlUrl, suggested);
          }}
          data-node-id="40004313:33316"
        >
          <span>이미지 다운로드</span>
          {icons.messager?.downloadBlue && (
            <img
              src={icons.messager.downloadBlue}
              alt=""
              className={styles.imageDownloadIcon}
              width={18}
              height={18}
            />
          )}
        </button>
      </div>
    </div>
  );
}

export default ChatImagePreviewModal;
